import { useState } from 'react';
import { Download, Plus, List, FolderOpen, CheckCircle, ArrowLeft, FileText } from 'lucide-react';
import { IssueListView } from '../issues/IssueListView';
import { FileGroupedView } from '../issues/FileGroupedView';
import { AddIssueModal } from '../issues/AddIssueModal';
import { PackageSummaryView } from '../summary/PackageSummaryView';
import { NotesSection } from '../notes/NotesSection';
import { Badge } from '@/components/ui/badge';
import type { ViewMode, Issue } from '@/types/reportApp';
import type { ReportStatus } from '@/types/report';

interface ReportScreenProps {
  packageName: string;
  reportId: string;
  issues: Issue[];
  pendingIssues: Issue[];
  approvedIssues: Issue[];
  isLoadingReport: boolean;
  viewMode: ViewMode;
  editingIssue: string | null;
  showAddModal: boolean;
  toastMessage: string;
  reportStatus?: ReportStatus;
  showApproveButton?: boolean;
  isApprovingReport?: boolean;
  readOnly?: boolean;
  notes?: string | null;

  onViewModeChange: (mode: ViewMode) => void;
  onSetEditingIssue: (id: string | null) => void;
  onSetShowAddModal: (show: boolean) => void;
  onHandleUpdateIssue: (id: string, updates: Partial<Issue>) => void;
  onHandleApprove: (id: string) => void;
  onHandleMoveToPending: (id: string) => void;
  onHandleDismiss: (id: string) => void;
  onHandleAddIssue: (issue: Omit<Issue, 'id' | 'status'>) => void | Promise<void>;
  onHandleDownloadPDF: () => void;
  onHandleApproveReport?: () => void;
  onHandleNotesUpdate?: (notes: string) => Promise<void>;
  onBack?: () => void;
}

// Helper function to get status badge styling
const getStatusBadgeProps = (status: ReportStatus) => {
  switch (status) {
    case 'incoming':
      return { label: 'Incoming', className: 'bg-gray-100 text-gray-700' };
    case 'preqc':
      return { label: 'Pre-QC Failed', className: 'bg-red-100 text-red-700' };
    case 'qc-pending':
      return { label: 'QC Pending', className: 'bg-yellow-100 text-yellow-700' };
    case 'qc-reviewed':
      return { label: 'QC Reviewed', className: 'bg-green-100 text-green-700' };
    default:
      return { label: status, className: 'bg-gray-100 text-gray-700' };
  }
};

export const ReportScreen = ({
  packageName,
  reportId,
  pendingIssues,
  approvedIssues,
  isLoadingReport,
  viewMode,
  editingIssue,
  showAddModal,
  toastMessage,
  reportStatus,
  showApproveButton = false,
  isApprovingReport = false,
  readOnly = false,
  notes,
  onViewModeChange,
  onSetEditingIssue,
  onSetShowAddModal,
  onHandleUpdateIssue,
  onHandleApprove,
  onHandleMoveToPending,
  onHandleDismiss,
  onHandleAddIssue,
  onHandleDownloadPDF,
  onHandleApproveReport,
  onHandleNotesUpdate,
  onBack,
}: ReportScreenProps) => {
  const [activeTab, setActiveTab] = useState<'issues' | 'summary'>('issues');
  const redCount = pendingIssues.filter(i => i.flag === 'red').length;
  const orangeCount = pendingIssues.filter(i => i.flag === 'orange').length;

  return (
    <div className="min-h-screen">
      <div className="p-6 pb-24">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Loading State */}
          {isLoadingReport ? (
            <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-12 text-center">
              <div className="animate-spin w-16 h-16 border-4 border-wppBlue border-t-transparent rounded-full mx-auto mb-6"></div>
              <h2 className="text-2xl font-semibold mb-2">Loading Report...</h2>
              <p className="text-gray-600">Fetching report data...</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {/* Back Button */}
                    {onBack && (
                      <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                      >
                        <ArrowLeft className="w-5 h-5" />
                        <span className="font-medium">Back</span>
                      </button>
                    )}

                    {/* Tab Toggle */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-1 flex gap-1 relative">
                      {/* Sliding background indicator */}
                      <div
                        className="absolute top-1 bottom-1 bg-blue-600 rounded shadow-sm transition-all duration-300 ease-in-out"
                        style={{
                          width: 'calc(50% - 2px)',
                          left: activeTab === 'issues' ? '4px' : 'calc(50% + 0px)',
                        }}
                      />
                      <button
                        onClick={() => setActiveTab('issues')}
                        className={`px-3 py-1.5 rounded font-medium text-sm transition-colors relative z-10 ${
                          activeTab === 'issues' ? 'text-white' : 'text-gray-700'
                        }`}
                      >
                        <List className="w-4 h-4 inline mr-1" />
                        Pending Issues
                      </button>
                      <button
                        onClick={() => setActiveTab('summary')}
                        className={`px-3 py-1.5 rounded font-medium text-sm transition-colors relative z-10 ${
                          activeTab === 'summary' ? 'text-white' : 'text-gray-700'
                        }`}
                      >
                        <FileText className="w-4 h-4 inline mr-1" />
                        Package Summary
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">

                  {!readOnly && reportStatus !== 'preqc' && (
                    <button
                      onClick={() => onSetShowAddModal(true)}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-wppBlue hover:text-white transition-all"
                    >
                      <Plus className="w-4 h-4 inline mr-2" />
                      Add Issue
                    </button>
                  )}

                  <button
                    onClick={onHandleDownloadPDF}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-wppBlue hover:text-white transition-all"
                  >
                    <Download className="w-4 h-4 inline mr-2" />
                    Download PDF
                  </button>

                  {showApproveButton && onHandleApproveReport && reportStatus !== 'preqc' && (
                    <button
                      onClick={onHandleApproveReport}
                      disabled={isApprovingReport || pendingIssues.length > 0}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title={pendingIssues.length > 0 ? `Cannot approve: ${pendingIssues.length} pending issue${pendingIssues.length === 1 ? '' : 's'} remaining` : 'Approve report'}
                    >
                      <CheckCircle className="w-4 h-4 inline mr-2" />
                      {isApprovingReport ? 'Approving...' : 'Approve'}
                    </button>
                  )}
                </div>
                </div>

                {/* View Mode Toggle (only visible when on Issues tab) */}
                {activeTab === 'issues' && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-1 flex gap-1 relative inline-flex">
                    {/* Sliding background indicator */}
                    <div
                      className="absolute top-1 bottom-1 bg-blue-600 rounded shadow-sm transition-all duration-300 ease-in-out"
                      style={{
                        width: 'calc(50% - 2px)',
                        left: viewMode === 'issue' ? '4px' : 'calc(50% + 0px)',
                      }}
                    />
                    <button
                      onClick={() => onViewModeChange('issue')}
                      className={`px-3 py-1.5 rounded font-medium text-sm transition-colors relative z-10 ${
                        viewMode === 'issue' ? 'text-white' : 'text-gray-700'
                      }`}
                    >
                      <List className="w-4 h-4 inline mr-1" />
                      Issue View
                    </button>
                    <button
                      onClick={() => onViewModeChange('file')}
                      className={`px-3 py-1.5 rounded font-medium text-sm transition-colors relative z-10 ${
                        viewMode === 'file' ? 'text-white' : 'text-gray-700'
                      }`}
                    >
                      <FolderOpen className="w-4 h-4 inline mr-1" />
                      File View
                    </button>
                  </div>
                )}
              </div>

              {/* Report Header */}
              <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold mb-2">
                      Quality Control Report
                    </h1>
                    <div className="space-y-1">
                      <p className="text-gray-600">Ford Puma Ready for Anything Campaign</p>
                      {packageName && (
                        <p className="text-sm text-gray-400">
                          <span className="text-gray-400">Package:</span> <span className="font-mono font-semibold text-blue-600">{packageName}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400 mb-1">Report ID</p>
                    <p className="font-mono font-semibold text-lg text-blue-600 mb-2">{reportId || 'N/A'}</p>
                    {reportStatus && (
                      <Badge className={getStatusBadgeProps(reportStatus).className}>
                        {getStatusBadgeProps(reportStatus).label}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-white/5 rounded-lg p-4 border border-gray-200">
                    <p className="text-xs text-gray-400 mb-1">Pending</p>
                    <p className="text-3xl font-semibold">{pendingIssues.length}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 border border-gray-200">
                    <p className="text-xs text-gray-400 mb-1">Red Flags</p>
                    <p className="text-3xl font-semibold text-[#EF4444]">{redCount}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 border border-gray-200">
                    <p className="text-xs text-gray-400 mb-1">Orange Flags</p>
                    <p className="text-3xl font-semibold text-[#F97316]">{orangeCount}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 border border-gray-200">
                    <p className="text-xs text-gray-400 mb-1">Approved</p>
                    <p className="text-3xl font-semibold text-[#10B981]">{approvedIssues.length}</p>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              {onHandleNotesUpdate && (
                <NotesSection
                  reportId={reportId}
                  notes={notes ?? null}
                  readOnly={readOnly}
                  onNotesUpdate={onHandleNotesUpdate}
                />
              )}

              {/* Content based on active tab */}
              {activeTab === 'issues' ? (
                <>
                  {/* No Issues - All Passed */}
                  {pendingIssues.length === 0 && approvedIssues.length === 0 && (
                    <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-12">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">All Validation Checks Passed</h3>
                        <p className="text-gray-600">No issues found in this campaign package.</p>
                      </div>
                    </div>
                  )}

                  {/* Pending Issues */}
                  {pendingIssues.length > 0 && (
                    <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6">
                      <h2 className="text-xl font-semibold mb-4">
                        Pending Issues ({pendingIssues.length})
                      </h2>

                      {viewMode === 'issue' ? (
                        <IssueListView
                          issues={pendingIssues}
                          editingIssue={editingIssue}
                          readOnly={readOnly}
                          onEdit={onSetEditingIssue}
                          onCancelEdit={() => onSetEditingIssue(null)}
                          onUpdate={onHandleUpdateIssue}
                          onApprove={onHandleApprove}
                          onMoveToPending={onHandleMoveToPending}
                          onDismiss={onHandleDismiss}
                        />
                      ) : (
                        <FileGroupedView
                          issues={pendingIssues}
                          editingIssue={editingIssue}
                          readOnly={readOnly}
                          onEdit={onSetEditingIssue}
                          onCancelEdit={() => onSetEditingIssue(null)}
                          onUpdate={onHandleUpdateIssue}
                          onApprove={onHandleApprove}
                          onMoveToPending={onHandleMoveToPending}
                          onDismiss={onHandleDismiss}
                        />
                      )}
                    </div>
                  )}

                  {/* Approved Issues */}
                  {approvedIssues.length > 0 && (
                    <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6">
                      <h2 className="text-xl font-semibold mb-4 text-[#10B981]">
                        Approved Issues ({approvedIssues.length})
                      </h2>

                      {viewMode === 'issue' ? (
                        <IssueListView
                          issues={approvedIssues}
                          editingIssue={editingIssue}
                          readOnly={readOnly}
                          onEdit={onSetEditingIssue}
                          onCancelEdit={() => onSetEditingIssue(null)}
                          onUpdate={onHandleUpdateIssue}
                          onApprove={onHandleApprove}
                          onMoveToPending={onHandleMoveToPending}
                          onDismiss={onHandleDismiss}
                        />
                      ) : (
                        <FileGroupedView
                          issues={approvedIssues}
                          editingIssue={editingIssue}
                          readOnly={readOnly}
                          onEdit={onSetEditingIssue}
                          onCancelEdit={() => onSetEditingIssue(null)}
                          onUpdate={onHandleUpdateIssue}
                          onApprove={onHandleApprove}
                          onMoveToPending={onHandleMoveToPending}
                          onDismiss={onHandleDismiss}
                        />
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* Package Summary Tab */
                <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6">
                  <h2 className="text-xl font-semibold mb-4">
                    Package Summary
                  </h2>
                  <PackageSummaryView reportId={reportId} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-8 right-8 bg-wppBlue text-white rounded-lg px-6 py-3 shadow-lg z-50">
          <p className="text-sm font-medium">{toastMessage}</p>
        </div>
      )}

      {/* Add Issue Modal */}
      {showAddModal && <AddIssueModal onClose={() => onSetShowAddModal(false)} onAdd={onHandleAddIssue} />}
    </div>
  );
};
